import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../components/Modal';
import styles from './UploadExternalVersionModal.module.scss';
import cx from 'classnames';
import CloseButton from '../../../../components/CloseButton/CloseButton';
import UploadDropzone from '../UploadDropzone/UploadDropzone';
import { CustomButton } from '../../../../components/Button/Button';
import VormatsAPI from '../../../../../common/apis/VormatsAPI';
import { ToastNotification } from '../../../../components/Toast/Toast';

const MAX_NAME_LENGTH = 100;

const UploadExternalVersionModal = ({ storyId, open, onUploaded, onClose }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [file, setFile] = useState(null);


    const handleDrop = (acceptedFiles, rejectedFiles) => {
        console.log('handleDrop', { acceptedFiles, rejectedFiles });

        if (acceptedFiles?.length + rejectedFiles?.length > 1) {
            ToastNotification({
                message: t('clip_organizer.add_modal.files_simultaneously', {
                    number: MAX_FILES_AMOUNT,
                }),
            });
            return;
        }

        rejectedFiles.forEach(({ file }) => {
            ToastNotification({
                message: `${file.name} ${t('clip_organizer.add_modal.wrong_file_format')} ${t('clip_organizer.add_modal.supported_files')}`,
            });
        });

        if (acceptedFiles[0]?.size > 4294967296) {
            ToastNotification({
                message: `${acceptedFiles.name} ${t('clip_organizer.add_modal.file_is_too_large')} ${t(
                    'clip_organizer.add_modal.supported_files'
                )}`,
            });
            return;
        }

        acceptedFiles.length && setFile(acceptedFiles[0]);
    }

    const handleClose = useCallback(() => {
        setFile(null);
        setIsUploading(false);
        setShowErrorMessage(false);
        setName('');
        onClose();
    }, [onClose])

    const onUpload = useCallback(async () => {
        setIsUploading(true);
        try {
            const url = await VormatsAPI.uploadExternalEditedVideo(storyId, file);
            await VormatsAPI.createExternalVideoVersion(storyId, name, url);
            onUploaded();
            handleClose();
        } catch (e) {
            console.log('Error while uploading: ', e);
            ToastNotification({
                message: `${t('video.player.error_message_two')}`,
            });
        } finally {
            setIsUploading(false);
        }
    }, [storyId, file, name]);

    return (
        <Modal open={open} onClose={handleClose} className={styles.wrapper} >
            <div className={styles.headerContainer}>
                <h1 className={styles.title}>
                    {t('edit_external.upload.modal_header')}
                </h1>
                <CloseButton variant='link' disabled={isUploading} onClose={handleClose} />
            </div>
            <div className={cx(styles.nameContainer)}>
                <p className={styles.description}> {t('edit_external.upload.name_description')}</p>

                <input
                    className={styles.input}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('edit_external.upload.name.placeholder')}
                    disabled={isUploading}
                    maxLength={MAX_NAME_LENGTH}
                />

            </div>
            <UploadDropzone
                className={styles.dropzone}
                mainTitle={t('clip_organizer.add_modal.instructions_drag_drop_one_video')}
                progressTitle={t('clip_organizer.add_modal.uploading')}
                onDrop={handleDrop}
                waitingForApproval={file !== null}
                isUploading={isUploading}
                showErrorMessage={showErrorMessage} />
            <div className={styles.btnContainer}>
                <CustomButton variant='secondary' onClick={handleClose} disabled={isUploading} label={t('general.cancel')} />
                <CustomButton variant='primary' disabled={!file || !name || isUploading} onClick={onUpload} label={t('clip_organizer.add_modal.upload')} />
            </div>
        </Modal>
    );
}

export default UploadExternalVersionModal;